
import { defineComponent, ref, watchEffect, inject, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Apollo } from '@/core/services';
import {
    GET_PRODUCT_GROUP_ASSIGNMENT_PRODUCTS,
    SEARCH_PRODUCT_GROUP_ASSIGNMENT_PRODUCTS
} from '@/modules/catalog/product-groups/graphql/Queries';
import Search from '@/components/search/Search.vue';
import Table from '@/components/Table/Table.vue';
import InnerLoader from '@/components/InnerLoader.vue';

export default defineComponent({
    name: 'assignment-product-group',
    components: { Search, Table, InnerLoader },
    setup() {
        const i18n = useI18n();
        const store = useStore();
        const loader = ref(false);
        const exist = ref(false);
        const loading = ref(false);
        const productsData: any = ref([]);
        const filteredProductsData: any = ref([]);
        const unFilteredProductsData: any = ref([]);
        const pagination = ref({}) as Record<any, any>;
        const system_locale = ref();
        const emitter: any = inject('emitter');
        const clickHandler = ref() as Record<any, any>;
        const routeParam = ref(0);
        const columnName = ref(null);
        const columnSort = ref(null);
        const searchQuery = ref(false);

        const columns = ref([
            {
                label: 'message.PRODUCT_NUMBER',
                key: 'economic_number',
                sorting: true
            },
            {
                label: 'message.NAME',
                key: 'name',
                sorting: true
            },
            {
                label: 'message.SALE_PRICE',
                key: 'sale_price',
                textAlignment: 'end'
            },
            {
                label: 'message.DISCOUNT',
                key: 'discount',
                textAlignment: 'center'
            }
        ]);

        const filterObject = ref([
            {
                label: 'message.NAME',
                value: 'name'
            },
            {
                label: 'message.PRODUCT_NUMBER',
                value: 'model'
            }
        ]);

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
            routeParam.value = Number(localStorage.getItem('prod_grp_id'));
        });

        const normalizeDiscountType = (type: unknown): string | null => {
            if (!type || typeof type !== 'string') return null;
            if (type === 'value') return 'amount';
            return type;
        };

        const formatDiscountType = (type: string | null) => {
            if (!type) return '';
            if (type === 'percentage') return i18n.t('message.PERCENTAGE');
            return i18n.t('message.AMOUNT');
        };

        const getSalePrice = (element: Record<string, unknown>) => {
            return (
                (element?.product_to_prices_admin as Record<string, unknown>[])?.[0]?.price ??
                (element?.product_to_prices as Record<string, unknown>[])?.[0]?.price ??
                null
            );
        };

        const buildProductRow = (
            element: Record<string, unknown>,
            productGroupId: number,
            assignment: Record<string, unknown> | null = null
        ) => {
            const matchingAssignment =
                assignment && Number(assignment.product_group_id) === productGroupId ? assignment : null;

            return {
                economic_number: element?.economic_number,
                name: element?.description,
                product_id: element?.id,
                product_group_id: productGroupId,
                sale_price: getSalePrice(element),
                discount_type: matchingAssignment
                    ? normalizeDiscountType(matchingAssignment.discount_type as string | undefined)
                    : null,
                discount_value: matchingAssignment
                    ? matchingAssignment.discount_value_original ?? matchingAssignment.discount_value ?? null
                    : null,
                assignment_id: matchingAssignment?.id ?? null
            };
        };

        const hasGroupDiscount = (item: Record<string, unknown>) => {
            return (
                Number(item.product_group_id) === Number(routeParam.value) &&
                !!item.discount_type &&
                item.discount_value !== null &&
                item.discount_value !== ''
            );
        };

        const buildProductsList = (data: Record<string, unknown>[], productGroupId: number) => {
            productsData.value = [];
            filteredProductsData.value = [];
            unFilteredProductsData.value = [];

            data.forEach(element => {
                exist.value = false;
                const assignments = (element.product_to_product_groups_admin as Record<string, unknown>[]) ?? [];

                assignments.forEach(ele => {
                    if (Number(ele.product_group_id) === productGroupId) {
                        filteredProductsData.value.push(buildProductRow(element, productGroupId, ele));
                        exist.value = true;
                    }
                });

                if (!exist.value) {
                    unFilteredProductsData.value.push(buildProductRow(element, productGroupId));
                }
            });

            productsData.value = filteredProductsData.value.concat(unFilteredProductsData.value);
        };

        const getProductGroupAssignmentProducts = (
            page = 1,
            reload = false,
            colName = null,
            type = null,
            id = Number(routeParam.value)
        ) => {
            loader.value = true;
            columnName.value = null;
            columnSort.value = null;

            Apollo.watchQuery({
                query: GET_PRODUCT_GROUP_ASSIGNMENT_PRODUCTS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-and-network',
                variables: {
                    page,
                    limit: 10,
                    id,
                    locale: system_locale.value
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                    return;
                }

                searchQuery.value = false;
                clickHandler.value = getProductGroupAssignmentProducts;
                reload === true ? (pagination.value.current_page = 1) : false;

                buildProductsList(data.product_group_assignment_products.data, id);
                pagination.value = data.product_group_assignment_products;
                loader.value = false;
            });
        };

        const searchHandler = (
            page = 1,
            reload = false,
            colName = null,
            type = null,
            id = Number(routeParam.value)
        ) => {
            if (colName != null) columnName.value = colName;
            if (type != null) columnSort.value = type;

            const searchData = store.getters.getSearchData;
            loader.value = true;

            Apollo.watchQuery({
                query: SEARCH_PRODUCT_GROUP_ASSIGNMENT_PRODUCTS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-and-network',
                variables: {
                    page,
                    limit: 10,
                    id,
                    filter: searchData?.data?.select,
                    search_key: searchData?.data?.input,
                    locale: system_locale.value,
                    col: columnName.value,
                    type: columnSort.value === true ? 'asc' : 'desc'
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                    return;
                }

                searchQuery.value = true;
                clickHandler.value = searchHandler;

                buildProductsList(data.search_product_group_assignment_products.data, id);
                pagination.value = data.search_product_group_assignment_products;
                loader.value = false;
            });
        };

        const handleCurrentChange = (num: number) => {
            clickHandler.value(num);
        };

        const handleEditDiscount = (item: Record<string, unknown>) => {
            emitter.emit('editProductGroupDiscountAction', item);
        };

        const refreshListing = () => {
            if (searchQuery.value) {
                searchHandler(pagination.value.current_page, false, columnName.value, columnSort.value, Number(routeParam.value));
            } else {
                getProductGroupAssignmentProducts(pagination.value.current_page, false, null, null, Number(routeParam.value));
            }
        };

        const onDiscountSaved = () => {
            refreshListing();
        };

        onMounted(() => {
            getProductGroupAssignmentProducts(1, false, null, null, Number(routeParam.value));
            emitter.on('productGroupDiscountSaved', onDiscountSaved);
        });

        onUnmounted(() => {
            emitter.off('productGroupDiscountSaved', onDiscountSaved);
        });

        return {
            pagination,
            filterObject,
            system_locale,
            productsData,
            columns,
            loading,
            loader,
            searchHandler,
            handleCurrentChange,
            handleEditDiscount,
            formatDiscountType,
            hasGroupDiscount,
            routeParam,
            getProductGroupAssignmentProducts
        };
    }
});
